// src/components/teacher/TeacherCourseDetail.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders } from "../../utils/api";
import {
  BookIcon,
  StudentsIcon,
  CalendarIcon,
  GradeIcon,
  ClockIcon,
  EditIcon,
  ViewIcon,
} from "../shared/icons/Icons";
import {
  USE_DUMMY_DATA,
  DUMMY_COURSES,
  DUMMY_TIMETABLE,
  studentsByCourseId,
} from "./dummyData";

/**
 * Detail view for a single assigned course. Shows the students in that class,
 * the course's timetable slots, and quick actions (attendance / grades).
 * The assignment is passed via navigation state; on a hard refresh it is
 * recovered from the `courses` prop using the assignment id in the URL.
 */
function TeacherCourseDetail({ courses = [], loading: parentLoading }) {
  const navigate = useNavigate();
  const { assignmentId } = useParams();
  const location = useLocation();

  const course =
    location.state?.course ||
    courses.find((c) => String(c.id) === String(assignmentId)) ||
    (USE_DUMMY_DATA
      ? DUMMY_COURSES.find((c) => String(c.id) === String(assignmentId))
      : null);

  const [students, setStudents] = useState([]);
  const [filterPool, setFilterPool] = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionFilter, setSessionFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");

  useEffect(() => {
    if (course?.class_name) setClassFilter(String(course.class_name));
  }, [course?.class_name, assignmentId]);

  useEffect(() => {
    if (!course) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);

      if (USE_DUMMY_DATA) {
        const dummy = studentsByCourseId(course.course_id || course.id);
        setFilterPool(dummy);
        const filtered = dummy.filter((s) => {
          if (
            sessionFilter &&
            String(s.academic_session || "").toLowerCase() !==
              sessionFilter.toLowerCase()
          ) {
            return false;
          }
          if (
            classFilter &&
            String(s.class_name || "").toLowerCase() !== classFilter.toLowerCase()
          ) {
            return false;
          }
          if (
            sectionFilter &&
            String(s.class_section || "").toLowerCase() !==
              sectionFilter.toLowerCase()
          ) {
            return false;
          }
          return true;
        });
        setStudents(filtered);
        setTimetable(
          DUMMY_TIMETABLE.filter(
            (t) => t.course_id === (course.course_id || course.id),
          ),
        );
        setLoading(false);
        return;
      }

      try {
        const baseParams = new URLSearchParams();
        if (course.course_id != null) baseParams.set("course_id", course.course_id);

        const filterParams = new URLSearchParams(baseParams);
        if (classFilter) filterParams.set("class_name", classFilter);
        if (sessionFilter) filterParams.set("academic_session", sessionFilter);
        if (sectionFilter) filterParams.set("section", sectionFilter);

        const [poolRes, studentsRes, timetableRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/teachers/me/students?${baseParams.toString()}`, {
            headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
          }),
          fetch(`${API_BASE_URL}/api/teachers/me/students?${filterParams.toString()}`, {
            headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
          }),
          fetch(`${API_BASE_URL}/api/teachers/me/timetable`, {
            headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
          }),
        ]);

        const poolData = poolRes.ok ? await poolRes.json() : [];
        setFilterPool(Array.isArray(poolData) ? poolData : []);

        const studentsData = studentsRes.ok ? await studentsRes.json() : [];
        setStudents(Array.isArray(studentsData) ? studentsData : []);

        const timetableData = timetableRes.ok ? await timetableRes.json() : [];
        const slots = (Array.isArray(timetableData) ? timetableData : []).filter(
          (t) =>
            (course.course_id == null || t.course_id === course.course_id) &&
            (course.class_id == null || t.class_id === course.class_id),
        );
        setTimetable(slots);
      } catch (err) {
        console.error("Failed to load course detail:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assignmentId,
    course?.course_id,
    course?.class_id,
    sessionFilter,
    classFilter,
    sectionFilter,
  ]);

  const sessionOptions = Array.from(
    new Set(filterPool.map((s) => s.academic_session).filter(Boolean)),
  ).sort();
  const classOptions = Array.from(
    new Set([
      ...(course?.class_name ? [course.class_name] : []),
      ...courses
        .filter((c) => String(c.course_id || "") === String(course?.course_id || ""))
        .map((c) => c.class_name)
        .filter(Boolean),
      ...filterPool.map((s) => s.class_name).filter(Boolean),
    ]),
  ).sort();
  const sectionOptions = Array.from(
    new Set(filterPool.map((s) => s.class_section).filter(Boolean)),
  ).sort();

  if (parentLoading || loading) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading course...</p>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="tp-empty">
        <BookIcon />
        <h3>Course not found</h3>
        <p>This course could not be loaded.</p>
        <button
          type="button"
          className="tp-btn"
          onClick={() => navigate("/teacher/courses")}
        >
          Back to My Courses
        </button>
      </div>
    );
  }

  const section = course.class_section || course.section;
  const activeCount = students.filter(
    (s) => s.student_status === "Active",
  ).length;

  return (
    <div>
      <div className="tp-page-header">
        <div>
          <h2>
            <span className="tp-title-icon"><BookIcon /></span>
            <span>{course.course_name || course.name}</span>
          </h2>
          <p>
            <span className="tp-chip tp-chip--class">
              {course.class_name || "No class"}
            </span>
            {section ? (
              <span className="tp-chip tp-chip--section" style={{ marginLeft: 6 }}>
                {section}
              </span>
            ) : null}
            {course.campus_name ? (
              <span style={{ marginLeft: 10, color: "var(--tp-muted)" }}>
                {course.campus_name}
              </span>
            ) : null}
          </p>
        </div>
      </div>

      <div className="tp-stats-grid">
        <div className="tp-card">
          <h3 className="tp-stat-value">{students.length}</h3>
          <p className="tp-stat-label">Total Students</p>
        </div>
        <div className="tp-card">
          <h3 className="tp-stat-value" style={{ color: "#16a34a" }}>
            {activeCount}
          </h3>
          <p className="tp-stat-label">Active</p>
        </div>
        <button
          type="button"
          className="tp-card tp-card--clickable"
          onClick={() =>
            navigate("/teacher/attendance", {
              state: {
                classId: course.class_id,
                courseId: course.course_id,
                courseName: course.course_name || course.name,
                className: course.class_name,
                section,
              },
            })
          }
        >
          <h3 className="tp-stat-value" style={{ color: "#1d4ed8" }}>
            <CalendarIcon />
          </h3>
          <p className="tp-stat-label">Take Attendance</p>
        </button>
        <button
          type="button"
          className="tp-card tp-card--clickable"
          onClick={() =>
            navigate("/teacher/grades", {
              state: { courseId: course.course_id },
            })
          }
        >
          <h3 className="tp-stat-value" style={{ color: "#7c3aed" }}>
            <GradeIcon />
          </h3>
          <p className="tp-stat-label">Enter Grades</p>
        </button>
      </div>

      <h3 className="tp-section-title">
        <span className="tp-title-icon"><BookIcon /></span>
        <span>Course Tools</span>
      </h3>
      <div className="tp-tool-grid">
        <button
          type="button"
          className="tp-tool-card"
          onClick={() =>
            navigate(`/teacher/courses/${assignmentId}/remarks`, { state: { course } })
          }
        >
          <span className="tp-tool-card__icon"><EditIcon /></span>
          <strong>Student Remarks</strong>
          <p>Add academic or behavioral remarks for students in this class.</p>
          <span className="tp-tool-card__cta">Open remarks →</span>
        </button>
        <button
          type="button"
          className="tp-tool-card"
          onClick={() =>
            navigate(`/teacher/courses/${assignmentId}/homework`, { state: { course } })
          }
        >
          <span className="tp-tool-card__icon"><BookIcon /></span>
          <strong>Homework</strong>
          <p>Assign homework to the whole class or selected students.</p>
          <span className="tp-tool-card__cta">Assign homework →</span>
        </button>
        <button
          type="button"
          className="tp-tool-card"
          onClick={() =>
            navigate(`/teacher/courses/${assignmentId}/materials`, { state: { course } })
          }
        >
          <span className="tp-tool-card__icon"><ViewIcon /></span>
          <strong>Lectures &amp; Files</strong>
          <p>Upload lectures, worksheets, and course files for this class.</p>
          <span className="tp-tool-card__cta">Manage files →</span>
        </button>
      </div>

      <h3 className="tp-section-title">
        <span className="tp-title-icon"><StudentsIcon /></span>
        <span>Students</span>
      </h3>
      <div className="tp-filters tp-student-filters">
        <label>
          Session
          <select value={sessionFilter} onChange={(e) => setSessionFilter(e.target.value)}>
            <option value="">All Sessions</option>
            {sessionOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label>
          Class
          <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
            <option value="">All Classes</option>
            {classOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)}>
            <option value="">All Sections</option>
            {sectionOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        {(sessionFilter || sectionFilter || (classFilter && classFilter !== (course.class_name || ""))) && (
          <button
            type="button"
            className="tp-btn tp-btn--ghost"
            onClick={() => {
              setSessionFilter("");
              setSectionFilter("");
              setClassFilter(course.class_name || "");
            }}
          >
            Clear Filters
          </button>
        )}
      </div>
      {students.length > 0 ? (
        <div className="tp-table-wrap">
          <table className="tp-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Father's Name</th>
                <th>Section</th>
                <th>Grade</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => (
                <tr
                  key={student.id}
                  className="tp-row-clickable"
                  onClick={() =>
                    navigate(`/teacher/courses/${assignmentId}/students/${student.id}`, {
                      state: { course, student },
                    })
                  }
                >
                  <td>
                    <span className="tp-mono">{student.student_id}</span>
                  </td>
                  <td style={{ fontWeight: 600 }}>{student.name}</td>
                  <td>{student.father_name || "N/A"}</td>
                  <td>
                    <span className="tp-chip tp-chip--section">
                      {student.class_section || section || "N/A"}
                    </span>
                  </td>
                  <td>{student.grade || "—"}</td>
                  <td>
                    <span
                      className={`tp-status ${
                        student.student_status === "Active"
                          ? "tp-status--active"
                          : "tp-status--withdrawn"
                      }`}
                    >
                      {student.student_status || "Active"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="tp-empty">
          <StudentsIcon />
          <h3>No students</h3>
          <p>No students are enrolled in this class yet.</p>
        </div>
      )}

      <h3 className="tp-section-title">
        <span className="tp-title-icon"><CalendarIcon /></span>
        <span>Timetable</span>
      </h3>
      {timetable.length > 0 ? (
        <div className="tp-table-wrap">
          <table className="tp-table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Period</th>
                <th>Time</th>
                <th>Room</th>
              </tr>
            </thead>
            <tbody>
              {timetable.map((slot) => (
                <tr key={slot.id}>
                  <td style={{ fontWeight: 600 }}>{slot.day_of_week}</td>
                  <td>
                    <span className="tp-chip tp-chip--class">
                      P{slot.period_number}
                    </span>
                  </td>
                  <td>
                    {slot.start_time && slot.end_time ? (
                      <span className="tp-mono">
                        <ClockIcon /> {slot.start_time} – {slot.end_time}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{slot.room ? `Room ${slot.room}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="tp-empty">
          <CalendarIcon />
          <h3>No timetable slots</h3>
          <p>This course has no scheduled periods yet.</p>
        </div>
      )}
    </div>
  );
}

export default TeacherCourseDetail;
